import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import type { TimeEntryItem } from '@hygge/shared';
import { entryDeleteLabel, entryDeleteSummary } from '../lib/entry-delete-copy';

interface DeleteEntryConfirmProps {
  /** The entry awaiting an answer; null while nothing is being deleted. */
  entry: TimeEntryItem | null;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: (entry: TimeEntryItem) => void;
}

/**
 * The question both delete paths ask before the entry is gone.
 *
 * `DELETE /time-entries/:id` is a hard delete with no undo, and the row's
 * trash icon is a 24×24 target that appears on hover right beside the
 * duration, on a row that is itself clickable — so a click meant for the row
 * used to destroy the entry instead of opening it. Saved reports in this same
 * module already confirm before deleting; a time entry, which feeds the
 * payroll-adjacent reports, was the one object that did not. [HW-843]
 *
 * One component for both callers, rather than a dialog per screen: the entry
 * being deleted is the same object whichever control started it, so the
 * sentence describing it should not depend on where the user clicked.
 */
export function DeleteEntryConfirm({
  entry,
  isPending,
  onCancel,
  onConfirm,
}: DeleteEntryConfirmProps) {
  return (
    <ConfirmDialog
      open={entry !== null}
      // Escape closes it even mid-flight. Holding the dialog open until the
      // answer arrives would be a trap rather than a safeguard: Cancel is
      // disabled while pending, an AlertDialog ignores clicks outside it, and
      // the API client sets no timeout — so a request that never comes back
      // would leave a modal with no exit at all, and on the Edit path the
      // dialog and the task panel behind it unreachable too. Closing early
      // abandons nothing: the delete is the server's now, and the hook
      // refreshes the lists on success whether this is still mounted or not.
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
      destructive
      title={
        // A description can be long; the title wraps rather than pushing the
        // dialog wide, the same way the skill-delete confirm handles it.
        <span className="wrap-anywhere">
          Delete “{entry ? entryDeleteLabel(entry) : ''}”?
        </span>
      }
      description={
        entry ? `${entryDeleteSummary(entry)}. This cannot be undone.` : undefined
      }
      confirmLabel="Delete"
      isPending={isPending}
      onConfirm={() => {
        if (entry) onConfirm(entry);
      }}
    />
  );
}
